import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CalendarClock, Lock, Clock } from "lucide-react";

interface LockedScheduledResultDialogProps {
  open: boolean;
  onClose: () => void;
  scheduledDate: string | null;
  scheduledTime: string | null;
}

const MONTH_LABELS_ES = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

function parseScheduled(date: string | null, time: string | null): Date | null {
  if (!date) return null;
  const [y, m, d] = date.split("-").map(Number);
  const [hh = 0, mm = 0] = (time || "00:00").split(":").map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d, hh, mm);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function formatLongDateEs(dt: Date): string {
  return dt.toLocaleDateString("es-DO", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

function format12hEs(dt: Date): string {
  return dt
    .toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .replace("AM", "AM")
    .replace("PM", "PM");
}

export function LockedScheduledResultDialog({
  open,
  onClose,
  scheduledDate,
  scheduledTime,
}: LockedScheduledResultDialogProps) {
  const dt = parseScheduled(scheduledDate, scheduledTime);
  const longDate = dt
    ? formatLongDateEs(dt).replace(/^./, (c) => c.toUpperCase())
    : "—";
  const time12 = dt ? format12hEs(dt) : "—";
  const monthLabel = dt ? MONTH_LABELS_ES[dt.getMonth()] : "—";
  const dayLabel = dt ? String(dt.getDate()) : "—";

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) onClose();
      }}
    >
      <DialogContent
        className="max-w-sm p-0 overflow-hidden"
        data-testid="dialog-locked-scheduled-result"
      >
        <div className="bg-gradient-to-br from-primary/15 via-background to-background px-6 pt-7 pb-5 flex flex-col items-center text-center gap-3 border-b">
          <div className="relative w-16 h-16 rounded-full bg-background border flex items-center justify-center shadow-sm">
            <CalendarClock className="w-7 h-7 text-primary" />
            <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-zinc-900 text-white flex items-center justify-center border-2 border-background">
              <Lock className="w-3.5 h-3.5" />
            </div>
          </div>
          <DialogTitle
            className="text-lg font-bold leading-tight"
            data-testid="text-locked-result-title"
          >
            Aún no puedes registrar el resultado
          </DialogTitle>
          <DialogDescription className="text-sm leading-relaxed">
            Espera a que el partido termine para cargar los sets.
          </DialogDescription>
        </div>

        <div className="px-6 py-5">
          <div className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground mb-2 text-center">
            Se desbloquea el
          </div>
          <div className="rounded-md border border-primary/30 bg-primary/5 p-3 flex items-center gap-3">
            <div className="flex flex-col items-center justify-center bg-background rounded-md border w-14 h-14 shrink-0">
              <span className="text-[10px] font-bold text-primary uppercase leading-none">
                {monthLabel}
              </span>
              <span
                className="text-xl font-black text-foreground leading-none"
                data-testid="text-locked-result-day"
              >
                {dayLabel}
              </span>
            </div>
            <div className="flex flex-col">
              <span
                className="text-base font-bold text-foreground"
                data-testid="text-locked-result-date"
              >
                {longDate}
              </span>
              <span
                className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5"
                data-testid="text-locked-result-time"
              >
                <Clock className="w-3.5 h-3.5" /> {time12}
              </span>
            </div>
          </div>
        </div>

        <DialogFooter className="p-4 border-t sm:justify-end">
          <Button
            onClick={onClose}
            data-testid="button-locked-result-close"
          >
            Entendido
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function isScheduledMatchLocked(
  scheduledDate: string | null | undefined,
  scheduledTime: string | null | undefined,
): boolean {
  if (!scheduledDate) return false;
  const [y, m, d] = scheduledDate.split("-").map(Number);
  const [hh = 0, mm = 0] = (scheduledTime || "00:00").split(":").map(Number);
  if (!y || !m || !d) return false;
  const dt = new Date(y, m - 1, d, hh, mm);
  if (Number.isNaN(dt.getTime())) return false;
  return dt.getTime() > Date.now();
}
